import { Component, inject, Input } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { UpdateClientDto } from '../../dto/updateClientDto';
import { ClientDto } from '../../../../api';
import { ClientStore } from '../../services/client.store';
import { LabelComponent } from '../../../../shared/components/label/label.component';
import { InputErrorMessageComponent } from '../../../../shared/components/input-error-message/input-error-message.component';

export function uniqueClientNameValidator(
  clients: ClientDto[],
  currentClientId?: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim().toLowerCase();
    if (!value) return null;
    const exists = clients.some((c) => c.name.toLowerCase() === value && c.id !== currentClientId);
    return exists ? { clientNameExists: true } : null;
  };
}

@Component({
  selector: 'app-client-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    TextareaModule,
    LabelComponent,
    InputErrorMessageComponent,
  ],
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.css'],
})
export class ClientFormComponent {
  private readonly clientStore = inject(ClientStore);

  readonly clientsOptions = this.clientStore.clients;

  @Input() set client(value: ClientDto | undefined) {
    this._client = value;
    this.loadClientData();
  }
  private _client: ClientDto | undefined = undefined;

  @Input() set clients(value: ClientDto[]) {
    this._clients = value ?? [];
    this.updateNameValidator();
  }
  private _clients: ClientDto[] = [];

  @Input() onValidityChange?: (isInvalid: boolean) => void;

  clientForm = new FormGroup({
    id: new FormControl<string | null>(null),

    name: new FormControl('', [
      Validators.required,
      Validators.maxLength(200),
    ]),

    phone: new FormControl('', [
      Validators.pattern(/^\+?[1-9]\d{7,14}$/),
      Validators.maxLength(20),
    ]),

    email: new FormControl('', [
      Validators.email,
      Validators.maxLength(255),
    ]),

    note: new FormControl('', [
      Validators.maxLength(500),
    ]),
  });

  constructor() {
    this.clientForm.statusChanges.subscribe(() => {
      this.emitValidity();
    });
  }

  private emitValidity(): void {
    if (this.onValidityChange) {
      this.onValidityChange(this.clientForm.invalid);
    }
  }

  private loadClientData(): void {
    if (this._client) {
      this.clientForm.patchValue(
        {
          id: this._client.id,
          name: this._client.name,
          phone: this._client.phone ?? '',
          email: this._client.email ?? '',
          note: this._client.note ?? '',
        },
        { emitEvent: false },
      );
    } else {
      this.clientForm.reset({}, { emitEvent: false });
    }

    this.updateNameValidator();
    this.emitValidity();
  }

  private updateNameValidator(): void {
    const nameControl = this.clientForm.get('name');
    if (!nameControl) return;

    const validators = [Validators.required];
    if (this._clients.length > 0) {
      validators.push(uniqueClientNameValidator(this._clients, this._client?.id));
    }

    nameControl.setValidators(validators);
    nameControl.updateValueAndValidity({ emitEvent: false });
  }

  getData(): UpdateClientDto {
    const v = this.clientForm.value;
    return {
      id: v.id!,
      name: v.name!.trim(),
      email: v.email?.trim() || undefined,
      phone: v.phone?.trim() || undefined,
      note: v.note?.trim() || undefined,
    };
  }

  isValid(): boolean {
    return this.clientForm.valid;
  }

  markAllAsTouched(): void {
    this.clientForm.markAllAsTouched();
    this.emitValidity();
  }

  hasError(field: string, type?: string): boolean {
    const ctrl = this.clientForm.get(field);
    if (!ctrl || !ctrl.touched) return false;
    return type ? ctrl.hasError(type) : ctrl.invalid;
  }

  getErrorMessage(field: string): string | null {
    const ctrl = this.clientForm.get(field);
    if (!ctrl || !ctrl.errors || !ctrl.touched) return null;

    if (ctrl.hasError('required')) return 'To pole jest wymagane';
    if (ctrl.hasError('email')) return 'Nieprawidłowy format email';
    if (ctrl.hasError('clientNameExists')) return 'Klient o tej nazwie już istnieje';

    return 'Nieprawidłowa wartość';
  }
}
